/**
 * How stock labels are laid out for printing, as configured under Company Settings.
 *
 * 'zebra' prints one label per page at exactly the label size, for a Zebra (or any
 * label printer) driven through its Windows driver from the browser print dialog.
 * 'sheet' is the original layout: fixed size labels tiled across an A4 page.
 */
export type LabelPrintMode = 'zebra' | 'sheet';

export interface LabelLayout {
  mode: LabelPrintMode;
  widthMm: number;
  heightMm: number;
}

/** Blacks load 100 x 50 mm stock, and it is the common shelf/product label size. */
export const DEFAULT_LABEL_LAYOUT: LabelLayout = { mode: 'zebra', widthMm: 100, heightMm: 50 };

/** Sizes offered on the settings screen. Anything else is entered as a custom size. */
export const LABEL_SIZE_PRESETS: { name: string, widthMm: number, heightMm: number }[] = [
  { name: '100 x 50 mm', widthMm: 100, heightMm: 50 },
  { name: '100 x 150 mm (4 x 6")', widthMm: 100, heightMm: 150 },
  { name: '102 x 76 mm (4 x 3")', widthMm: 102, heightMm: 76 },
  { name: '50 x 25 mm', widthMm: 50, heightMm: 25 },
];

export const MIN_LABEL_MM = 20;
export const MAX_LABEL_MM = 300;

/** Space kept clear round the edge of a Zebra label, so nothing prints off the stock. */
const PADDING_MM = 2;

/**
 * Reads the label layout off an environment_settings row. A missing row (a fresh
 * install), a row saved before the label columns existed, or a size outside what any
 * label printer takes all fall back to the default rather than printing nonsense.
 */
export function labelLayoutFrom(settings: any): LabelLayout {
  if (!settings) {
    return { ...DEFAULT_LABEL_LAYOUT };
  }

  const mode: LabelPrintMode = settings.labelPrintMode === 'sheet' ? 'sheet' : 'zebra';
  const widthMm = Number(settings.labelWidthMm);
  const heightMm = Number(settings.labelHeightMm);

  if (!isLabelDimension(widthMm) || !isLabelDimension(heightMm)) {
    return { ...DEFAULT_LABEL_LAYOUT, mode };
  }

  return { mode, widthMm, heightMm };
}

export function isLabelDimension(mm: number): boolean {
  return Number.isFinite(mm) && mm >= MIN_LABEL_MM && mm <= MAX_LABEL_MM;
}

/** Taller than wide: the QR code goes on top with the text underneath. */
export function isPortraitLabel(layout: LabelLayout): boolean {
  return layout.heightMm > layout.widthMm;
}

/**
 * Side of the QR code on a Zebra label. As large as fits, because a bigger code scans
 * from further away, while leaving room for the text beside or beneath it.
 */
export function labelQrSizeMm(layout: LabelLayout): number {
  if (isPortraitLabel(layout)) {
    return Math.min(layout.widthMm - PADDING_MM * 2, Math.floor(layout.heightMm * 0.6));
  }

  return Math.min(layout.heightMm - PADDING_MM * 2, Math.floor(layout.widthMm / 2));
}

/** Text sizes in mm, scaled to the label so small stock stays legible and big stock is used. */
export function labelFontSizesMm(layout: LabelLayout): { title: number, subtitle: number, reference: number } {
  const base = isPortraitLabel(layout) ? layout.widthMm : layout.heightMm;

  return {
    title: round1(base * 0.11),
    subtitle: round1(base * 0.08),
    reference: round1(base * 0.06),
  };
}

/**
 * Print stylesheet for the label pages, added while one is open and removed when it
 * closes so that printing anything else in the app keeps its own page setup.
 */
export function labelPrintCss(layout: LabelLayout): string {
  // Bootstrap 4's print styles force the body to at least 992px wide and the page to A3.
  // On a 100mm label that makes Chrome shrink everything to fit, so both are undone here,
  // and the grey app background is kept off the paper.
  const noMinWidth = 'html, body, .container { min-width: 0 !important; } html, body { background: white !important; }';

  if (layout.mode === 'sheet') {
    return `@media print { @page { size: A4; margin: 8mm; } ${noMinWidth} }`;
  }

  const w = layout.widthMm + 'mm';
  const h = layout.heightMm + 'mm';

  return `@media print {
  @page { size: ${w} ${h}; margin: 0; }
  ${noMinWidth}
  html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
  .fullcontainer, .fullcontainer-noBg { margin: 0 !important; padding: 0 !important; }
  .fullcontainer > br { display: none !important; }
  .labelSheet { display: block !important; }
  app-stock-label { display: block; }
  app-stock-label + app-stock-label { break-before: page; }
  .stockLabel { width: ${w} !important; height: ${h} !important; border: 0 !important; margin: 0 !important; }
}`;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
